import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PhotoGrid from '../components/shared/PhotoGrid';
import FullScreenView from '../components/shared/FullScreenView';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';

export default function ArtistPortfolio() {
    const navigate = useNavigate();

    const [fetchState, setFetchState] = useState({
        loaded: false,
        error: null,
        photos: [],
    });
    const [retryCount, setRetryCount] = useState(0);
    const [activeIndex, setActiveIndex] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    // Redirect if not logged in
    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/login', { replace: true });
        }
    }, [navigate]);

    // Fetch portfolio photos
    useEffect(() => {
        if (!localStorage.getItem('token')) return;
        let cancelled = false;
        apiFetch('/api/photos/portfolio', { headers: authHeaders() })
            .then(async (res) => {
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    throw new Error(body.message || `Portfolio request failed (${res.status})`);
                }
                return res.json();
            })
            .then((body) => {
                if (cancelled) return;
                setFetchState({ loaded: true, error: null, photos: body.data ?? [] });
            })
            .catch((err) => {
                if (cancelled) return;
                setFetchState({ loaded: true, error: err.message, photos: [] });
            });
            return () => { cancelled = true; };
    }, [retryCount]);

    const handleDownload = useCallback(async (photoId, filename) => {
        setDownloadingId(photoId);
        try {
            const res = await apiFetch(`/api/photos/portfolio/${photoId}/download`, {
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`Download failed (${res.status})`);
            const blob = await res.blob();
            const objectURL = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectURL;
            a.download = filename || 'photo';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectURL);
        } catch (err) {
            console.error(err);
            alert(`Could not download that photo. ${err.message}`);
        } finally {
            setDownloadingId(null);
        }
    }, []);


    const { loaded, error, photos } = fetchState;
    const activePhoto = activeIndex !== null ? photos[activeIndex] : null;

    if (!loaded) return <div style={{ padding: 24 }}>Loading your portfolio...</div>;

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <h2>We couldn't load your portfolio.</h2>
                <p style={{ color: '#7a1f1f' }}>{error}</p>
                <button
                    onClick={() => {
                        setFetchState({ loaded: false, error: null, photos: [] });
                        setRetryCount(c => c + 1);
                    }}
                    style={{ minHeight: 44, padding: '10px 20px' }}
                >
                    Try again
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <p style={{ color: '#444' }}>
                Click a photo to view full size, or use the Download button to save the high-resolution file.
            </p>

            {photos.length === 0 ? (
                <p style={{ color: '#444' }}>
                    No approved photos yet.
                </p>
            ) : (
                <PhotoGrid
                    photos={photos}
                    onSelect={setActiveIndex}
                    columns={3}
                    renderOverlay={(photo) => (
                        <button
                            disabled={downloadingId === photo.id}
                            title='Download original'
                            style={{
                                background: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '4px 8px',
                                fontSize: 18,
                                lineHeight: 1,
                                cursor: downloadingId === photo.id ? 'default' : 'pointer',
                                minHeight: 32,
                            }}
                            onClick={() => handleDownload(photo.id, photo.renamedFilename || photo.originalFilename)}
                        >
                            {downloadingId === photo.id ? '…' : '⬇'}
                        </button>
                    )}
                />
            )}

            <FullScreenView
                photo={activePhoto}
                onClose={() => setActiveIndex(null)}
                onPrev={() => setActiveIndex(i => Math.max(0, i - 1))}
                onNext={() => setActiveIndex(i => Math.min(photos.length - 1, i + 1))}
                hasPrev={activeIndex > 0}
                hasNext={activeIndex !== null && activeIndex < photos.length - 1}
                actionArea={
                    activePhoto && (
                        <button
                            onClick={() => handleDownload(activePhoto.id, activePhoto.renamedFilename || activePhoto.originalFilename)}
                            disabled={downloadingId === activePhoto.id}
                            style={{
                                minHeight: 44, padding: '12px 28px', fontSize: 16,
                                background: '#1F3A5F', color: 'white',
                                border: 'none', borderRadius: 4, cursor: 'pointer',
                            }}
                        >
                            {downloadingId === activePhoto.id ? 'Downloading...' : 'Download original'}
                        </button>
                    )
                }
            />
        </div>
    );
}